import React, { FC, Fragment, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    TextField,
} from '@mui/material';
import AddTechnicianForm from './add-technician-form.component';
import { useTranslation } from 'react-i18next';
import { User } from 'models/user';
import { useFormik } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { getUserByEmailTemplate } from 'apollo/templates/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { permissions } from 'utils/permission';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import LinkUserFormComponent from '../../shared/link-user-form.component';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';

type AddTechniciansDialogProps = {
    isDialogOpen: boolean;
    onDialogCloseClick: () => void;
    onAddTechnicianClick: () => void;
    onUserLinked?: () => void;
};

const AddTechniciansDialog: FC<AddTechniciansDialogProps> = ({
    isDialogOpen,
    onDialogCloseClick,
    onAddTechnicianClick,
    onUserLinked,
}: AddTechniciansDialogProps) => {
    const { t: translate } = useTranslation(['users']);
    const [activeStep, setActiveStep] = useState(0);
    const [notAllowedMsg, setNotAllowedMsg] = useState('');
    const [user, setUser] = useState<User>({} as User);
    const currentOrganization = currentOrgVar();
    const [isIsolatedUsersEnable, setIsIsolatedUsersEnable] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getUserByEmail, { loading }] = useLazyQuery(getUserByEmailTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ userByEmail: user }) => {
            if (!user) {
                setActiveStep(1);
                return;
            }
            setUser(user);
            if (user.organization?._id === currentOrganization._id) {
                // User already exists in organization
                setActiveStep(3);
                return;
            }

            if (user.isShared) {
                // No allowed
                setNotAllowedMsg('NOT_ALLOWED_SHARED');
                setActiveStep(4);
                return;
            }
            const isDentistOrExternalTech =
                permissions.isDentist(user.type) ||
                permissions.isLabTechnician(user.type) ||
                permissions.isExternalTechnician(user.role);
            if (
                isDentistOrExternalTech ||
                (permissions.isWhiteLabeledOrganization(user.organization) && permissions.isDentist(user.type))
            ) {
                // Link
                const hasIsolatedUsers = customerSettings.getCustomerSetting(
                    user.organization.customerSettings || [],
                    CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
                );
                if (hasIsolatedUsers) {
                    // No allowed
                    setNotAllowedMsg('NOT_ALLOWED_CLINIC');
                    setActiveStep(4);
                    return;
                }
                setActiveStep(2);
                return;
            }
            // No allowed
            setNotAllowedMsg('NOT_ALLOWED');
            setActiveStep(4);
        },
    });

    const validate = (values: { email: string }) => {
        const errors: Record<string, string> = {};
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validate,
        onSubmit: (values: { email: string }) => {
            getUserByEmail({ variables: { email: values.email } });
        },
    });

    const moveToFirstStep = () => {
        setActiveStep(0);
    };

    const userLinked = () => {
        onDialogCloseClick();
        if (onUserLinked) {
            onUserLinked();
        }
    };

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return (
                    <Fragment>
                        <form onSubmit={formik.handleSubmit}>
                            <DialogContent>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="email"
                                        label={translate('common:EMAIL')}
                                        required
                                        type="email"
                                        helperText={formik.touched.email && formik.errors.email}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        {...formik.getFieldProps('email')}
                                    />
                                </FormControl>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => onDialogCloseClick()}>{translate('common:CANCEL')}</Button>
                                <LoadingButton type="submit" variant="contained" loading={loading}>
                                    {translate('common:NEXT_STEP')}
                                </LoadingButton>
                            </DialogActions>
                        </form>
                    </Fragment>
                );
            case 1:
                return (
                    <AddTechnicianForm
                        onDialogCloseClick={onDialogCloseClick}
                        onAddTechnicianClick={onAddTechnicianClick}
                        email={formik.values.email}
                        isIsolatedUsersEnable={isIsolatedUsersEnable}
                    />
                );
            case 2:
                return (
                    <LinkUserFormComponent
                        userType="TECHNICIAN"
                        user={user}
                        onBack={moveToFirstStep}
                        onUserLinked={userLinked}
                    />
                );
            case 3:
                return (
                    <DialogContent>
                        <Box>
                            <Alert severity="warning">{translate('users:USER_ALREADY_EXISTS')}</Alert>
                        </Box>
                        <DialogActions>
                            <Button onClick={() => onDialogCloseClick()}>{translate('common:CANCEL')}</Button>
                            <Button onClick={moveToFirstStep} variant="contained">
                                {translate('common:BACK')}
                            </Button>
                        </DialogActions>
                    </DialogContent>
                );
            case 4:
                return (
                    <DialogContent>
                        <Box>
                            <Alert severity="warning">{translate(`users:${notAllowedMsg}`)}</Alert>
                        </Box>
                        <DialogActions>
                            <Button onClick={() => onDialogCloseClick()}>{translate('common:CANCEL')}</Button>
                            <Button onClick={moveToFirstStep} variant="contained">
                                {translate('common:BACK')}
                            </Button>
                        </DialogActions>
                    </DialogContent>
                );
            default:
                return 'Unknown Step';
        }
    };

    useEffect(() => {
        // Reset on dialog open
        if (isDialogOpen) {
            formik.resetForm();
            setNotAllowedMsg('');
            setUser({} as User);
            const hasIsolatedUsers = customerSettings.getCustomerSetting(
                currentOrganization.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
            );
            if (hasIsolatedUsers) {
                setActiveStep(1);
                setIsIsolatedUsersEnable(true);
            } else {
                setActiveStep(0);
                setIsIsolatedUsersEnable(false);
            }
        }
    }, [isDialogOpen]);

    return (
        <Dialog open={isDialogOpen} onClose={onDialogCloseClick} fullWidth maxWidth="md">
            <DialogTitle>{translate('users:ADD_USER')}</DialogTitle>
            {getStepContent(activeStep)}
        </Dialog>
    );
};

export default AddTechniciansDialog;
